import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
  orderBy,
  query,
  addDoc,
  where,
  writeBatch,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore'
import { addMonths, monthStart } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import {
  breakdownRemainder,
  COMPETENCE_IMPACTS,
  deriveCashImpact,
  deriveSettlementDate,
} from '@domain/finance'
import type { CategorySource, OffsetLink, Transaction } from '@domain/finance'
import type { Cents } from '@shared/money/cents'
import type {
  EnrichmentPatch,
  NewManualTransaction,
  TransactionRepository,
} from '@domain/ports/finance-repository'
import {
  oneOfOrNull,
  optionalCivilDate,
  optionalText,
  requireCents,
  requireCivilDate,
  requireInteger,
  requireText,
  requireIso,
  requireOneOf,
  subDocument,
  textList,
  textOr,
  toIso,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * As duas visões de `Docs/03-Arquitetura/Competência e caixa.md`, como consulta
 * do Firestore.
 *
 * ## As duas consultas, que não são disjuntas
 *
 * - **Competência** recorta por `source.transactionDate` — a data da compra — e
 *   fica com os `cashImpact` da coluna de competência da tabela da nota, que
 *   são `direct` e `deferred`.
 * - **Caixa** recorta por `settlementDate`. Não filtra `cashImpact` nenhum, e a
 *   ausência do filtro é o desenho: `deferred` tem `settlementDate` `null` para
 *   sempre (decisão 94), e uma faixa de string nunca casa com `null`, então a
 *   compra no cartão cai fora sozinha. Um segundo filtro seria uma segunda
 *   fonte de verdade para um fato que já está no dado.
 *
 * As duas **não** particionam o livro, e não é isso que o modelo promete:
 * `direct` é ✅ nas duas colunas, então um Pix de 05/07 volta nas duas
 * consultas do mês de julho — um movimento só, duas perguntas diferentes, a
 * mesma resposta. O que o modelo promete é mais estreito, e é o que fecha a
 * contagem dupla: `deferred` e `settlement` nunca caem na mesma visão. Ver a
 * decisão 96(b).
 *
 * ## O modelo mora em `domain/finance/monthly-view.ts`, não aqui
 *
 * `COMPETENCE_IMPACTS` é a lista que vira o `where(… 'in' …)`, e o predicado
 * `isInCompetence` ao lado dela é o mesmo recorte em memória. As nove linhas da
 * definição de pronto da fase exercitam o predicado; o teste de emulador roda
 * **esta** consulta sobre um livro semeado e compara o resultado com aquele
 * predicado. Sem isso seriam duas implementações do modelo central da fase sem
 * nada comparando uma com a outra, que foi o achado da revisão da Task 7.
 *
 * ## Leitura, e a edição do `enrichment` — não mais que isso
 *
 * `firestore.rules` tem `allow create: if false` em `transactions` — decisão da
 * Fase 1, e lançamento manual é da Fase 5. A prova de que a regra nega é
 * `tests/rules/firestore.rules.test.ts`, caso "membro não cria transação".
 *
 * `allow update` **já existe** desde a Fase 1, com `untouched()` em `source`,
 * `cashImpact`, `settlementDate`, `invoiceId` e `status` — então editar
 * categoria e observação não precisou de regra nova. O que a Fase 5 acrescenta
 * aqui é o caminho de escrita, e ele é estreito de propósito: ver
 * `EnrichmentPatch`.
 */

const SOURCE_KINDS = ['pluggy', 'manual'] as const
const INSTRUMENT_TYPES = ['account', 'creditCard'] as const
const PAYMENT_METHODS = ['pix', 'debit', 'credit', 'boleto', 'transfer', 'cash', 'other'] as const
const CASH_IMPACTS = ['direct', 'deferred', 'settlement', 'internal'] as const

/**
 * **A lista acima é de runtime, e o `tsc` não a liga sozinho ao tipo.**
 *
 * Ela é o que `requireOneOf` usa para recusar valor desconhecido vindo do
 * Firestore — e por isso precisa cobrir a união **inteira**: um membro que
 * exista no tipo e falte aqui faz toda transação com aquele valor **lançar na
 * leitura**, e nenhum teste de tipo pega, porque o array é `as const` e não
 * está declarado como sendo a união.
 *
 * Aconteceu: `internal` entrou em `Transaction['cashImpact']` em 2026-08-31, o
 * `tsc` apontou três lugares (o alias, a tabela do teste e o mapa de rótulos da
 * tela) e **não apontou este** — a lista teria ficado com três membros e a
 * leitura quebraria na primeira transação espelho.
 *
 * A linha abaixo fecha isso: ela só compila se os dois conjuntos forem
 * exatamente iguais, nos dois sentidos.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _cashImpactsCobreAUniao: Equals<
  Transaction['cashImpact'],
  (typeof CASH_IMPACTS)[number]
> = true
void _cashImpactsCobreAUniao
const STATUSES = ['pending', 'posted', 'canceled'] as const

function transactionsPath(): string {
  return `households/${HOUSEHOLD_ID}/transactions`
}

function toInstrument(value: unknown, where: string): Transaction['source']['instrument'] {
  const instrument = subDocument(value)
  return {
    // Conta e cartão nunca se misturam num campo só — é o `type` que decide se
    // a transação é `deferred`, e portanto se ela entra no caixa.
    type: requireOneOf(instrument['type'], INSTRUMENT_TYPES, `${where}.type`),
    id: requireText(instrument['id'], `${where}.id`),
  }
}

function toInstallment(value: unknown, where: string): Transaction['source']['installment'] {
  if (value === undefined || value === null) return null
  const installment = subDocument(value)
  return {
    number: requireInteger(installment['number'], `${where}.number`),
    total: requireInteger(installment['total'], `${where}.total`),
    groupId: optionalText(installment['groupId']),
  }
}

function toSource(value: unknown, where: string): Transaction['source'] {
  const source = subDocument(value)
  return {
    kind: requireOneOf(source['kind'], SOURCE_KINDS, `${where}.kind`),
    externalId: optionalText(source['externalId']),
    providerItemId: optionalText(source['providerItemId']),
    instrument: toInstrument(source['instrument'], `${where}.instrument`),
    description: textOr(source['description'], ''),
    // O sinal mora aqui e só aqui, e é o que separa saída de entrada. Sem
    // default: um valor corrompido virando 0 sairia de toda soma sem uma linha
    // de erro, com a soma continuando a fechar.
    amountCents: requireCents(source['amountCents'], `${where}.amountCents`),
    currency: 'BRL',
    // A data da COMPRA, que é quem decide competência. Obrigatória: uma
    // transação sem ela não pertence a mês nenhum.
    transactionDate: requireCivilDate(source['transactionDate'], `${where}.transactionDate`),
    postedAt: optionalCivilDate(source['postedAt'], `${where}.postedAt`),
    paymentMethod: oneOfOrNull(source['paymentMethod'], PAYMENT_METHODS),
    installment: toInstallment(source['installment'], `${where}.installment`),
    raw: source['raw'] ?? null,
    // Instante que o PROVIDER diz sobre a própria atualização — string, não
    // carimbo nosso. Ausente é `null`, que é o caso de todo lançamento manual.
    providerUpdatedAt: optionalText(source['providerUpdatedAt']),
    syncedAt: requireIso(source['syncedAt'], `${where}.syncedAt`),
  }
}

/**
 * De onde veio a categoria. **Ausente é `null`**, e `null` lê como manual: toda
 * transação categorizada antes da Fase 10 foi decidida por gente, e tratá-las
 * como automáticas jogaria 266 documentos numa fila de conferência no dia do
 * deploy. Formato quebrado também vira `null`, pelo mesmo motivo de sempre —
 * a tela não morre por causa de um campo torto.
 */
function toCategorySource(value: unknown): CategorySource | null {
  if (typeof value !== 'object' || value === null) return null
  const v = value as Record<string, unknown>
  const kind = v['kind']
  if (kind !== 'manual' && kind !== 'auto') return null
  return {
    kind,
    ruleId: optionalText(v['ruleId']),
    confirmedAt: toIso(v['confirmedAt']),
  }
}

function toEnrichment(
  value: unknown,
  amountCents: Cents,
  where: string,
): Transaction['enrichment'] {
  const enrichment = subDocument(value)
  const breakdown = enrichment['breakdown']

  return {
    merchantId: optionalText(enrichment['merchantId']),
    categoryId: optionalText(enrichment['categoryId']),
    categorySource: toCategorySource(enrichment['categorySource']),
    // Bloco ausente é o estado de uma transação que ninguém detalhou ainda, e
    // o resumo dela é exatamente o que `breakdownRemainder` devolve para zero
    // itens: nada alocado, e o resto é o valor inteiro em módulo. Escrever o
    // objeto à mão aqui seria repetir o invariante do resto num segundo lugar.
    breakdown:
      breakdown === undefined || breakdown === null
        ? breakdownRemainder(amountCents, [])
        : toBreakdown(breakdown, `${where}.breakdown`),
    receiptId: optionalText(enrichment['receiptId']),
    note: optionalText(enrichment['note']),
    tags: textList(enrichment['tags']),
    offsetOf: toOffsetLink(enrichment['offsetOf'], `${where}.offsetOf`),
    offset: toOffset(enrichment['offset'], `${where}.offset`),
  }
}

function toBreakdown(value: unknown, where: string): Transaction['enrichment']['breakdown'] {
  const breakdown = subDocument(value)
  return {
    itemCount: requireInteger(breakdown['itemCount'], `${where}.itemCount`),
    allocatedCents: requireCents(breakdown['allocatedCents'], `${where}.allocatedCents`),
    remainderCents: requireCents(breakdown['remainderCents'], `${where}.remainderCents`),
    // **Ausente é `{}`, e não erro.** As 2.728 transações que já estão em
    // produção têm o resumo sem este campo — ele nasceu depois delas —, e
    // exigi-lo faria a leitura do mês inteiro lançar. Vazio é a resposta certa:
    // sem item, nada foi alocado a categoria nenhuma.
    byCategory: toCentsMap(breakdown['byCategory'], `${where}.byCategory`),
  }
}

/**
 * O vínculo de uma entrada com a compra que ela abate.
 *
 * **Ausente é `null`** — o estado de toda transação de antes do abatimento
 * existir, e de quase todas depois. **Presente e torto lança**: é ele que tira
 * a entrada das somas, e um vínculo pela metade sumiria com dinheiro calado.
 * Só a descrição tolera ausência — ela é rótulo de tela, não muda número.
 */
function toOffsetLink(value: unknown, where: string): Transaction['enrichment']['offsetOf'] {
  if (value === undefined || value === null) return null
  const link = subDocument(value)
  return {
    purchaseKey: requireText(link['purchaseKey'], `${where}.purchaseKey`),
    transactionId: requireText(link['transactionId'], `${where}.transactionId`),
    description: textOr(link['description'], ''),
  }
}

/** Quanto as entradas abatem desta linha. Escrito só pelo gatilho; ver o tipo. */
function toOffset(value: unknown, where: string): Transaction['enrichment']['offset'] {
  if (value === undefined || value === null) return null
  const offset = subDocument(value)
  return {
    offsetCents: requireCents(offset['offsetCents'], `${where}.offsetCents`),
    entryCount: requireInteger(offset['entryCount'], `${where}.entryCount`),
  }
}

/**
 * `Record<string, Cents>` vindo do Firestore.
 *
 * **Cada valor passa por `requireCents`**, e não por um `as number`: é dinheiro,
 * e um `NaN` que atravessasse aqui sairia somado na rosca sem uma linha de erro
 * — com o gráfico continuando a desenhar. É a mesma linha que `toTransaction`
 * traça para `amountCents`.
 */
function toCentsMap(value: unknown, where: string): Record<string, Cents> {
  if (value === undefined || value === null) return {}
  const bruto = subDocument(value)
  const mapa: Record<string, Cents> = {}
  for (const [categoryId, cents] of Object.entries(bruto)) {
    mapa[categoryId] = requireCents(cents, `${where}.${categoryId}`)
  }
  return mapa
}

/**
 * Firestore → domínio, com **uma** conferência de consistência: compra no
 * cartão não tem data de caixa.
 *
 * É a decisão 94, e é a única linha deste arquivo que olha para dois campos ao
 * mesmo tempo. Está aqui porque é o dado que produz a contagem dupla que a
 * ADR-0004 existe para impedir: um documento `deferred` com `settlementDate`
 * preenchido volta de `listByCash` no mês do vencimento e soma junto com o
 * pagamento da fatura — R$ 1.000 onde saíram R$ 500 —, e nada no caminho
 * reclamaria. Falhar alto citando o documento é o que dá a alguém a chance de
 * consertar a origem.
 *
 * As outras duas linhas da tabela **não** ganham conferência: `direct` e
 * `settlement` têm `settlementDate` igual ao `transactionDate` hoje, mas isso é
 * o que `deriveSettlementDate` produz, não um invariante que a nota fixe para
 * sempre — pagamento parcial de fatura é da Fase 3 e pode mexer nisso. Cobrar o
 * que ainda vai ser decidido é como uma guarda vira um obstáculo.
 */
export function toTransaction(id: string, data: DocumentData): Transaction {
  const where = `transactions/${id}`
  const source = toSource(data['source'], `${where}.source`)
  const cashImpact = requireOneOf(data['cashImpact'], CASH_IMPACTS, `${where}.cashImpact`)
  const settlementDate = optionalCivilDate(data['settlementDate'], `${where}.settlementDate`)

  // Os DOIS impactos que ficam fora do caixa. `internal` entrou junto porque a
  // regra é a mesma: quem não entra no caixa não tem data de caixa, e um
  // `settlementDate` preenchido ali seria a transação espelho contando como
  // dinheiro que saiu — em cima do que a conta já contou. Decisão 138.
  if ((cashImpact === 'deferred' || cashImpact === 'internal') && settlementDate !== null) {
    throw new Error(
      `transaction-repository: ${where} é '${cashImpact}' e tem settlementDate ${settlementDate}. ` +
        'Nenhum dos dois entra no caixa — quem entra é o pagamento da fatura, que é ' +
        "'settlement'. Ver as decisões 94 e 138, e Docs/03-Arquitetura/Competência e caixa.md.",
    )
  }

  return {
    id,
    householdId: HOUSEHOLD_ID,
    source,
    cashImpact,
    settlementDate,
    invoiceId: optionalText(data['invoiceId']),
    enrichment: toEnrichment(data['enrichment'], source.amountCents, `${where}.enrichment`),
    status: requireOneOf(data['status'], STATUSES, `${where}.status`),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

/**
 * A faixa de um mês num campo `YYYY-MM-DD`: fechada embaixo, aberta em cima.
 *
 * `< monthStart(mês seguinte)` e não `<= último dia`: o limite de cima sai do
 * mesmo `addMonths` que já existe, então fevereiro, ano bissexto e a virada do
 * ano caem todos na mesma expressão, sem ninguém precisar saber quantos dias
 * tem o mês. `monthStart` valida a forma `YYYY-MM`, então um mês torto para
 * aqui em vez de virar uma consulta que devolve zero linhas.
 */
function monthRange(month: ReferenceMonth): { start: string; end: string } {
  return { start: monthStart(month), end: monthStart(addMonths(month, 1)) }
}

async function run(q: Query): Promise<Transaction[]> {
  const snap = await getDocs(q)
  return snap.docs.map((transaction) => toTransaction(transaction.id, transaction.data()))
}

/**
 * *Quanto consumimos neste mês?*
 *
 * `in` e não `!= 'settlement'`, e a escolha tem duas consequências práticas:
 * o `in` é filtro de igualdade, então a consulta tem **uma** desigualdade só
 * (a faixa de data) e o índice composto é o `cashImpact + source.transactionDate`
 * declarado em `firestore.indexes.json`; e a ordenação pode ser pela data, que é
 * a ordem que a lista de um mês quer. Com `!=`, `cashImpact` também vira
 * desigualdade e o Firestore exige que a primeira ordenação seja por ele.
 *
 * **A construção fica separada da execução** para que
 * `firestore-indexes.test.ts` derive o índice exigido do objeto `Query` que a
 * produção manda, e não de uma cópia dos `where` reescrita no teste — cópia
 * concorda com ela mesma para sempre.
 *
 * `orderBy` no mesmo campo da faixa, e não num campo qualquer: consulta com
 * `orderBy` num campo ausente do documento **não devolve o documento**, e aqui
 * isso é inofensivo justamente porque o campo ordenado é o mesmo que a faixa
 * já exige. Uma transação sem `transactionDate` não pertence a mês nenhum, e
 * ficar de fora é a resposta certa.
 */
function competenceQuery(db: Firestore, month: ReferenceMonth): Query {
  const { start, end } = monthRange(month)
  return query(
    collection(db, transactionsPath()),
    where('cashImpact', 'in', COMPETENCE_IMPACTS),
    where('source.transactionDate', '>=', start),
    where('source.transactionDate', '<', end),
    orderBy('source.transactionDate'),
  )
}

async function listByCompetence(db: Firestore, month: ReferenceMonth): Promise<Transaction[]> {
  return run(competenceQuery(db, month))
}

/**
 * *Quanto saiu da conta neste mês?*
 *
 * Sem filtro de `cashImpact`: a faixa de string em `settlementDate` já deixa
 * `deferred` de fora, porque o valor dele é `null` e `null` não está em faixa
 * nenhuma de string na ordenação de tipos do Firestore. É o mesmo mecanismo que
 * `isInCash` usa em memória (`settlementDate !== null`), e o teste de emulador
 * confere que os dois concordam sobre o mesmo livro — a afirmação sobre o
 * comportamento do `null` é medida contra o emulador, não lida na documentação.
 *
 * **Um campo só, e por isso nenhuma entrada em `firestore.indexes.json`:** a
 * faixa e a ordenação caem ambas em `settlementDate`, e índice de campo único o
 * Firestore cria sozinho. Quem confere que continua sendo um campo só — e que a
 * ausência da entrada é conclusão, não esquecimento — é
 * `firestore-indexes.test.ts`.
 */
function cashQuery(db: Firestore, month: ReferenceMonth): Query {
  const { start, end } = monthRange(month)
  return query(
    collection(db, transactionsPath()),
    where('settlementDate', '>=', start),
    where('settlementDate', '<', end),
    orderBy('settlementDate'),
  )
}

async function listByCash(db: Firestore, month: ReferenceMonth): Promise<Transaction[]> {
  return run(cashQuery(db, month))
}

/**
 * As duas consultas por nome, e o motivo de elas serem exportadas.
 *
 * `firestore-indexes.test.ts` monta cada uma e **deriva** dela o índice que o
 * Firestore vai exigir — igualdade primeiro, faixa e ordenação por último —,
 * comparando o resultado com `firestore.indexes.json`. Sem este registro o teste
 * teria de reescrever os `where` num literal próprio, e trocar
 * `source.transactionDate` por `source.postedAt` aqui não moveria asserção
 * nenhuma.
 *
 * O emulador **não** cobra índice: medido, a suíte de adapters passa inteira com
 * o arquivo de índices vazio. Então esta lista, e o teste que a lê, são a única
 * coisa neste repositório que reprova quando o arquivo discorda das consultas.
 */
export const TRANSACTION_QUERIES: Record<
  string,
  (db: Firestore, month: ReferenceMonth) => Query
> = {
  listByCompetence: competenceQuery,
  listByCash: cashQuery,
  // Ignora o `month` — não recorta por mês, e é justamente o ponto dela. Entra
  // aqui do mesmo jeito porque o guarda conta `query()` montados em `src/`, e
  // consulta fora do registro é consulta cujo índice ninguém derivou. A
  // derivação dela é o caso trivial: uma igualdade só, coberta pelo índice de
  // campo único que o Firestore mantém sozinho — nada a declarar no arquivo.
  listUncategorized: (db) => uncategorizedQuery(db),
  // Também ignora o `month`. O guarda deriva o índice da FORMA da consulta, e
  // o valor do grupo não muda a forma — daí um grupo de exemplo qualquer.
  listByInstallmentGroup: (db) => installmentGroupQuery(db, 'pluggy:exemplo:0000:1'),
  // Também ignora o `month`; a chave de exemplo só dá forma à consulta.
  listOffsetEntries: (db) => offsetEntriesQuery(db, 'pluggy_exemplo'),
}

async function byId(db: Firestore, id: string): Promise<Transaction | null> {
  const snap = await getDoc(doc(db, transactionsPath(), id))
  // Ausente é `null`, não erro: um link colado para transação que não existe
  // mais é o caso normal, e a tela sabe dizer isso melhor que um `throw`.
  return snap.exists() ? toTransaction(snap.id, snap.data()) : null
}

/**
 * **O patch vira um objeto de caminhos pontilhados, e a razão é dura.**
 *
 * `updateDoc(ref, { enrichment: {...} })` **substitui o bloco inteiro**: mandar
 * só `categoryId` apagaria `note`, `breakdown`, `tags` e `receiptId` da
 * transação. Com `'enrichment.categoryId'` o Firestore mexe num campo só.
 *
 * E o objeto é montado campo a campo, sem `undefined` no meio: o SDK recusa
 * `undefined` como valor, então um campo omitido tem de **não estar** no objeto,
 * e não estar ali com `undefined`.
 */
export function enrichmentUpdate(patch: EnrichmentPatch): Record<string, unknown> {
  const update: Record<string, unknown> = {}
  if (patch.categoryId !== undefined) update['enrichment.categoryId'] = patch.categoryId
  if (patch.note !== undefined) update['enrichment.note'] = patch.note
  if (patch.merchantId !== undefined) update['enrichment.merchantId'] = patch.merchantId
  if (patch.categorySource !== undefined) update['enrichment.categorySource'] = patch.categorySource
  return update
}

/**
 * "Confirmar todas" da fila de conferência (Fase 10).
 *
 * **Só o `confirmedAt`, e por caminho pontilhado.** Escrever o objeto
 * `categorySource` inteiro exigiria ler cada transação antes para saber qual
 * regra a categorizou — quarenta leituras para carimbar uma data. E `kind` e
 * `ruleId` ficam: conferir não é escolher, e a diferença entre "o app decidiu e
 * eu concordei" e "eu decidi" é o que a tela de regras vai ler depois.
 */
async function confirmAutoCategory(db: Firestore, ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const lote = writeBatch(db)
  for (const id of ids) {
    lote.update(doc(db, transactionsPath(), id), {
      'enrichment.categorySource.confirmedAt': serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }
  await lote.commit()
}

async function updateEnrichment(db: Firestore, id: string, patch: EnrichmentPatch): Promise<void> {
  const update = enrichmentUpdate(patch)
  // Patch vazio não vira escrita. Sem isto, um `updateDoc` só com `updatedAt`
  // marcaria a transação como editada sem nada ter mudado — e o sync, que
  // compara `updatedAt`, passaria a ver movimento onde não houve.
  if (Object.keys(update).length === 0) return
  await updateDoc(doc(db, transactionsPath(), id), {
    ...update,
    // `serverTimestamp()` e não o relógio do aparelho: dois celulares editando
    // a mesma transação precisam de uma ordem, e a do cliente pode estar
    // errada. As regras não cobram este campo — a disciplina é daqui.
    updatedAt: serverTimestamp(),
  })
}

/**
 * *O que ainda não foi classificado?*
 *
 * Uma igualdade só, e de propósito: `enrichment.categoryId == null` é coberto
 * pelo índice de campo único que o Firestore mantém sozinho. Acrescentar
 * "só saídas" exigiria uma faixa em `source.amountCents` e, com ela, um índice
 * composto declarado à mão — para separar duas coisas que a tela separa de
 * graça sobre uma lista que ela já tem em memória.
 *
 * **`null` explícito, e não campo ausente.** O adapter e a ingestão escrevem
 * `categoryId: null` desde sempre; se algum dia alguém gravar a transação sem
 * a chave, ela some desta lista sem erro nenhum — o mesmo silêncio que o
 * `clearedAt` dos itens de lista já custou uma vez.
 */
function uncategorizedQuery(db: Firestore): Query {
  return query(collection(db, transactionsPath()), where('enrichment.categoryId', '==', null))
}

async function listUncategorized(db: Firestore): Promise<Transaction[]> {
  return run(uncategorizedQuery(db))
}

/**
 * As parcelas de uma compra. Igualdade num campo **aninhado** — a derivação do
 * índice está em `firestore-indexes.test.ts`: o automático de campo único
 * cobre `source.installment.groupId` porque nenhum `fieldOverride` isenta o
 * mapa `source`.
 */
function installmentGroupQuery(db: Firestore, groupId: string): Query {
  return query(
    collection(db, transactionsPath()),
    where('source.installment.groupId', '==', groupId),
  )
}

async function listByInstallmentGroup(db: Firestore, groupId: string): Promise<Transaction[]> {
  return run(installmentGroupQuery(db, groupId))
}

/**
 * O limite de operações de um lote do Firestore.
 *
 * Não é folga escolhida: passar de 500 o `commit()` recusa o lote inteiro, e
 * "uber" sozinho tem 111 transações — dois grupos grandes numa tela já passam
 * disso.
 */
const LOTE = 500

/**
 * O mesmo `patch` em várias transações.
 *
 * **Lotes sequenciais, não paralelos.** Disparar vinte `commit()` de uma vez
 * sobre a mesma coleção é justamente o padrão que o Firestore penaliza por
 * contenção — e aqui não há nada a ganhar com isso: quem espera é uma tela em
 * que a pessoa acabou de tocar uma vez.
 *
 * **Sem transação.** Um lote que falhe no meio deixa parte do grupo
 * classificado, o que é um estado honesto e visível: a tela recarrega e mostra
 * o que sobrou. Envolver 2455 documentos numa transação seria trocar isso por
 * uma falha que desfaz tudo — e o Firestore nem permite, o teto é o mesmo 500.
 */
async function updateEnrichmentMany(
  db: Firestore,
  ids: string[],
  patch: EnrichmentPatch,
): Promise<void> {
  const update = enrichmentUpdate(patch)
  if (Object.keys(update).length === 0 || ids.length === 0) return

  for (let i = 0; i < ids.length; i += LOTE) {
    const batch = writeBatch(db)
    for (const id of ids.slice(i, i + LOTE)) {
      batch.update(doc(db, transactionsPath(), id), { ...update, updatedAt: serverTimestamp() })
    }
    await batch.commit()
  }
}

/**
 * As entradas ligadas a uma compra. Igualdade num campo aninhado de
 * `enrichment` — nenhum `fieldOverride` isenta o mapa, então o índice de campo
 * único cobre (ver `firestore-indexes.test.ts`).
 */
function offsetEntriesQuery(db: Firestore, purchaseKey: string): Query {
  return query(
    collection(db, transactionsPath()),
    where('enrichment.offsetOf.purchaseKey', '==', purchaseKey),
  )
}

async function listOffsetEntries(db: Firestore, purchaseKey: string): Promise<Transaction[]> {
  return run(offsetEntriesQuery(db, purchaseKey))
}

/**
 * Liga e desliga, por caminho pontilhado — o resto do `enrichment` fica onde
 * está. Em lotes de `LOTE`, sequenciais, pelo mesmo motivo de
 * `updateEnrichmentMany`; na prática a folha nunca passa de dezenas.
 */
async function setOffsetLinks(
  db: Firestore,
  changes: { id: string; offsetOf: OffsetLink | null }[],
): Promise<void> {
  for (let i = 0; i < changes.length; i += LOTE) {
    const batch = writeBatch(db)
    for (const { id, offsetOf } of changes.slice(i, i + LOTE)) {
      batch.update(doc(db, transactionsPath(), id), {
        'enrichment.offsetOf': offsetOf,
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
  }
}

/**
 * O documento de um lançamento manual.
 *
 * **Os derivados saem das funções do domínio, encadeadas** — `deriveCashImpact`
 * classifica e `deriveSettlementDate` recebe a classificação que ela produziu.
 * É a mesma cadeia da ingestão e da fixture, e é o que impede este adapter de
 * ser uma terceira opinião sobre o modelo central do app. Escrever
 * `cashImpact: 'direct'` à mão aqui compilaria, passaria, e divergiria no dia em
 * que a derivação mudasse.
 *
 * `firestore.rules` recusa o documento se os dois não forem coerentes com o
 * instrumento — é a segunda camada, e ela existe justamente porque esta função
 * poderia estar errada.
 *
 * **`syncedAt` recebe o instante da criação**, não `null`: o campo é obrigatório
 * no tipo, e para um lançamento manual "quando este registro entrou no sistema"
 * é agora. O que diz que ele não veio de provider é `kind: 'manual'`, com
 * `externalId` e `providerItemId` nulos — os três campos que a regra confere.
 */
function manualDocument(input: NewManualTransaction, agora: unknown): DocumentData {
  const source: Transaction['source'] = {
    kind: 'manual',
    externalId: null,
    providerItemId: null,
    instrument: { type: 'account', id: input.accountId },
    description: input.description,
    amountCents: input.amountCents,
    currency: 'BRL',
    transactionDate: input.transactionDate,
    // Lançamento manual não tem "data de lançamento no banco" — quem lançou foi
    // uma pessoa, e a data da compra é a única que ela conhece.
    postedAt: null,
    paymentMethod: input.paymentMethod,
    installment: null,
    raw: null,
    providerUpdatedAt: null,
    syncedAt: '',
  }
  const cashImpact = deriveCashImpact(source, { settlesInvoiceId: null })

  return {
    source: { ...source, syncedAt: agora },
    cashImpact,
    settlementDate: deriveSettlementDate(source, cashImpact),
    // Nulo, e a regra cobra: quem agrupa fatura é a ingestão. Ver
    // `NewManualTransaction`.
    invoiceId: null,
    enrichment: {
      merchantId: null,
      categoryId: input.categoryId,
      // Pela função do domínio: o resumo de zero itens é `breakdownRemainder`
      // quem define, e o resto é o valor em módulo.
      breakdown: breakdownRemainder(input.amountCents, []),
      receiptId: null,
      note: input.note,
      tags: [],
    },
    status: 'posted',
    createdAt: agora,
    updatedAt: agora,
  }
}

/** A porta com o `db` já preso, para a composição em `src/main.tsx`. */
export function createFirebaseTransactionRepository(db: Firestore): TransactionRepository {
  return {
    listByCompetence: (month) => listByCompetence(db, month),
    listByCash: (month) => listByCash(db, month),
    byId: (id) => byId(db, id),
    updateEnrichment: (id, patch) => updateEnrichment(db, id, patch),
    listUncategorized: () => listUncategorized(db),
    listByInstallmentGroup: (groupId) => listByInstallmentGroup(db, groupId),
    listOffsetEntries: (purchaseKey) => listOffsetEntries(db, purchaseKey),
    setOffsetLinks: (changes) => setOffsetLinks(db, changes),
    updateEnrichmentMany: (ids, patch) => updateEnrichmentMany(db, ids, patch),
    confirmAutoCategory: (ids) => confirmAutoCategory(db, ids),
    createManual: async (input) => {
      const created = await addDoc(
        collection(db, transactionsPath()),
        manualDocument(input, serverTimestamp()),
      )
      return created.id
    },
  }
}
